import {
  PROTOCOL_VERSION,
  type RpcEnvelope,
  type RpcReply,
  type StorageRequest,
  type StorageResult,
} from './protocol';

interface PortLike {
  postMessage(message: unknown, transfer?: Transferable[]): void;
  onmessage: ((e: MessageEvent) => void) | null;
}

type ResultOf<R extends StorageRequest> = Extract<StorageResult, { op: R['op'] }>;

/** Promise-based RPC to the storage worker, over a Worker or MessagePort. */
export class StorageRpc {
  private nextId = 1;
  private readonly pending = new Map<
    number,
    { resolve: (r: StorageResult) => void; reject: (e: unknown) => void }
  >();

  constructor(private readonly port: PortLike) {
    port.onmessage = (e: MessageEvent) => {
      const m = e.data as RpcReply | undefined;
      if (!m || m.v !== PROTOCOL_VERSION || typeof m.id !== 'number') return;
      const p = this.pending.get(m.id);
      if (!p) return;
      this.pending.delete(m.id);
      if (m.ok) p.resolve(m.result);
      else p.reject(new DOMException(m.error.message, m.error.name));
    };
  }

  call<R extends StorageRequest>(payload: R, transfer: Transferable[] = []): Promise<ResultOf<R>> {
    const id = this.nextId++;
    return new Promise<ResultOf<R>>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (r: StorageResult) => void, reject });
      const env: RpcEnvelope<StorageRequest> = { v: PROTOCOL_VERSION, id, payload };
      this.port.postMessage(env, transfer);
    });
  }

  /** Reject everything in flight, e.g. when the worker died. */
  failAll(error: unknown): void {
    for (const p of this.pending.values()) p.reject(error);
    this.pending.clear();
  }
}

/** Transfer a chunk without copying only when it exclusively owns its buffer. */
export function transferable(data: Uint8Array): { data: Uint8Array; transfer: Transferable[] } {
  if (
    data.byteOffset === 0 &&
    data.byteLength === data.buffer.byteLength &&
    data.buffer instanceof ArrayBuffer
  ) {
    return { data, transfer: [data.buffer] };
  }
  const copy = data.slice();
  return { data: copy, transfer: [copy.buffer] };
}
